import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Inbox, LogOut, Mail, RefreshCw, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTERS = ["all", "new", "contacted", "closed"];

const PILL = {
  new: "bg-brand/15 text-brand border-brand/40",
  contacted: "bg-amber-400/10 text-amber-400 border-amber-400/40",
  closed: "bg-slate-500/10 text-slate-400 border-slate-500/40",
};

export default function AdminInbox() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("et_admin_token")}` });

  const handle401 = useCallback(() => {
    localStorage.removeItem("et_admin_token");
    navigate("/admin/login");
  }, [navigate]);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/enquiries`, { headers: authHeader() });
      setItems(data);
    } catch (e) {
      if (e.response?.status === 401) handle401();
      else toast.error("Failed to load enquiries");
    }
  }, [handle401]);

  useEffect(() => {
    if (!localStorage.getItem("et_admin_token")) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [load, navigate]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/enquiries/${id}`, { status }, { headers: authHeader() });
      toast.success(`Marked as ${status}`);
      load();
    } catch (e) {
      if (e.response?.status === 401) handle401();
      else toast.error("Could not update status");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/enquiries/${id}`, { headers: authHeader() });
      toast.success("Enquiry deleted");
      load();
    } catch (e) {
      if (e.response?.status === 401) handle401();
      else toast.error("Could not delete enquiry");
    }
  };

  const logout = () => {
    localStorage.removeItem("et_admin_token");
    navigate("/admin/login");
  };

  const visible = (items || []).filter((i) => filter === "all" || i.status === filter);
  const counts = (items || []).reduce((acc, i) => ({ ...acc, [i.status]: (acc[i.status] || 0) + 1 }), {});

  return (
    <div className="mx-auto max-w-6xl px-6 py-16 md:px-12" data-testid="admin-inbox-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-brand">Private</p>
          <h1 className="mt-2 flex items-center gap-3 text-3xl font-bold text-white">
            <Inbox className="h-7 w-7 text-brand" /> Enquiry Inbox
          </h1>
        </div>
        <div className="flex gap-3">
          <button
            onClick={load}
            data-testid="inbox-refresh-button"
            className="inline-flex items-center gap-2 rounded-md border border-navy-600 px-4 py-2 text-sm text-slate-300 transition-colors hover:border-brand/60 hover:text-brand"
          >
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
          <button
            onClick={logout}
            data-testid="inbox-logout-button"
            className="inline-flex items-center gap-2 rounded-md border border-navy-600 px-4 py-2 text-sm text-slate-300 transition-colors hover:border-red-400/60 hover:text-red-400"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="inbox-filter-tabs">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`filter-tab-${f}`}
            className={`rounded-md px-4 py-1.5 font-mono text-xs uppercase tracking-wider transition-colors ${
              filter === f ? "bg-brand text-navy" : "border border-navy-600 text-slate-400 hover:border-brand/60"
            }`}
          >
            {f} {f === "all" ? `(${items?.length ?? 0})` : `(${counts[f] || 0})`}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-4">
        {items === null && <p className="text-sm text-slate-400">Loading enquiries...</p>}
        {items !== null && visible.length === 0 && (
          <div className="rounded-lg border border-navy-600 bg-navy-800 p-10 text-center text-sm text-slate-400" data-testid="inbox-empty">
            No enquiries in this view.
          </div>
        )}
        {visible.map((enq) => (
          <div key={enq.id} className="rounded-lg border border-navy-600 bg-navy-800 p-6" data-testid={`enquiry-card-${enq.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-bold text-white">{enq.company}</h3>
                <p className="mt-0.5 text-sm text-slate-400">
                  {enq.contact_name} · <a href={`mailto:${enq.email}`} className="text-brand hover:underline">{enq.email}</a>
                </p>
              </div>
              <span className={`rounded-full border px-3 py-1 font-mono text-xs uppercase ${PILL[enq.status] || PILL.new}`} data-testid={`enquiry-status-${enq.id}`}>
                {enq.status}
              </span>
            </div>
            <div className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
              <p className="text-slate-300"><span className="text-slate-500">Domain:</span> {enq.product_domain}</p>
              <p className="text-slate-300"><span className="text-slate-500">Volume:</span> {enq.volume || "-"}</p>
              <p className="text-slate-300"><span className="text-slate-500">Port:</span> {enq.destination_port || "-"}</p>
            </div>
            <p className="mt-3 whitespace-pre-line rounded-md border border-navy-600/60 bg-navy p-4 text-sm text-slate-300">{enq.message}</p>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <p className="font-mono text-xs text-slate-500">{new Date(enq.created_at).toLocaleString()}</p>
              <div className="flex flex-wrap gap-2">
                <a
                  href={`mailto:${enq.email}?subject=RE: Your ${enq.product_domain} enquiry - Excellent Traders Pte Ltd`}
                  data-testid={`enquiry-reply-${enq.id}`}
                  className="inline-flex items-center gap-1.5 rounded-md border border-navy-600 px-3 py-1.5 text-xs text-slate-300 transition-colors hover:border-brand/60 hover:text-brand"
                >
                  <Mail className="h-3.5 w-3.5" /> Reply
                </a>
                {enq.status === "new" && (
                  <button onClick={() => setStatus(enq.id, "contacted")} data-testid={`enquiry-contacted-${enq.id}`}
                    className="rounded-md border border-amber-400/40 px-3 py-1.5 text-xs text-amber-400 transition-colors hover:bg-amber-400/10">
                    Mark contacted
                  </button>
                )}
                {enq.status !== "closed" && (
                  <button onClick={() => setStatus(enq.id, "closed")} data-testid={`enquiry-closed-${enq.id}`}
                    className="rounded-md border border-slate-500/40 px-3 py-1.5 text-xs text-slate-400 transition-colors hover:bg-slate-500/10">
                    Close
                  </button>
                )}
                <button onClick={() => remove(enq.id)} data-testid={`enquiry-delete-${enq.id}`}
                  className="inline-flex items-center gap-1.5 rounded-md border border-red-500/40 px-3 py-1.5 text-xs text-red-400 transition-colors hover:bg-red-500/10">
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
